import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { coachListConversations, coachPostWorkoutComment } from "../../lib/api";
import { WORKOUT_TYPES } from "../../lib/coaching";

function fmtTs(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function typeLabel(type) {
  const t = (WORKOUT_TYPES || []).find((x) => x.value === type);
  return t?.label || type || "";
}

function threadDomId(t) {
  return `conv-${t.workout_id}`;
}

function Bubble({ c }) {
  const isCoach = c.from_role === "coach";
  return (
    <div
      className={`flex ${isCoach ? "justify-end" : "justify-start"}`}
      data-testid={`conv-comment-${c.id}`}
    >
      <div
        className={`max-w-[85%] rounded-md px-3 py-2 ${
          isCoach
            ? "bg-[#FAF7F2] border border-[var(--tir-burgundy)]/20"
            : "bg-white border border-[var(--tir-border)]"
        }`}
      >
        <div className="flex items-baseline gap-2 mb-1">
          <span
            className={`text-[10px] tracking-[0.18em] uppercase font-semibold ${
              isCoach ? "text-[var(--tir-burgundy)]" : "text-[var(--tir-navy)]"
            }`}
          >
            {isCoach ? "Coach" : "Athlete"} · {c.from_name}
          </span>
          <span className="text-[9px] font-mono-tir text-[var(--tir-muted)]">
            {fmtTs(c.created_at)}
          </span>
        </div>
        <div className="text-[12px] text-[var(--tir-ink)] whitespace-pre-wrap leading-snug">
          {c.body}
        </div>
      </div>
    </div>
  );
}

function ThreadCard({ t, athleteId, highlighted }) {
  const [comments, setComments] = useState(t.comments || []);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (highlighted && ref.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, [highlighted]);

  const send = async (e) => {
    e?.preventDefault?.();
    const text = draft.trim();
    if (!text || posting) return;
    setPosting(true);
    try {
      const saved = await coachPostWorkoutComment(athleteId, {
        program_id: t.program_id,
        week_number: t.week_number,
        day_label: t.day_label,
        workout_id: t.workout_id,
        body: text,
      });
      setComments((prev) => [...prev, saved]);
      setDraft("");
      toast.success("Reply sent — your athlete will get an email");
    } catch {
      toast.error("Couldn't send — please try again");
    } finally {
      setPosting(false);
    }
  };

  const log = t.log;
  return (
    <div
      id={threadDomId(t)}
      ref={ref}
      className={`bg-white rounded-md border p-4 ${
        highlighted
          ? "border-[var(--tir-burgundy)] ring-2 ring-[var(--tir-burgundy)]/20"
          : "border-[var(--tir-border)]"
      }`}
      data-testid={`conversation-thread-${t.workout_id}`}
    >
      <div className="flex items-baseline justify-between gap-2 flex-wrap mb-1">
        <div className="min-w-0">
          <span className="text-[13px] font-bold text-[var(--tir-ink)]">
            {t.workout_title || typeLabel(t.workout_type) || "Workout"}
          </span>
          <span className="ml-2 text-[10px] tracking-[0.18em] uppercase font-mono-tir text-[var(--tir-muted)]">
            W{t.week_number} · {t.day_label}
            {t.program_title ? ` · ${t.program_title}` : ""}
          </span>
        </div>
        <span className="text-[10px] font-mono-tir text-[var(--tir-muted)]">
          {fmtTs(t.last_at)}
        </span>
      </div>
      {log?.athlete_note && (
        <div className="text-[11px] text-[var(--tir-muted)] italic mb-2 leading-snug border-l-2 border-[var(--tir-border)] pl-2">
          Log note: &ldquo;{log.athlete_note}&rdquo;
        </div>
      )}
      <div className="space-y-2 mb-3 max-h-72 overflow-y-auto pr-1">
        {comments.map((c) => (
          <Bubble key={c.id} c={c} />
        ))}
      </div>
      <form onSubmit={send} className="flex gap-2 items-start">
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={2}
          placeholder="Reply to your athlete… (they'll get an email + push)"
          data-testid={`conversation-reply-input-${t.workout_id}`}
          className="flex-1 border border-[var(--tir-border)] bg-white px-2 py-1.5 text-[12px] focus:outline-none focus:border-[var(--tir-burgundy)] rounded-sm leading-snug resize-y"
        />
        <button
          type="submit"
          disabled={!draft.trim() || posting}
          data-testid={`conversation-reply-send-${t.workout_id}`}
          className="inline-flex items-center gap-1 text-[10px] tracking-[0.22em] uppercase font-semibold px-3 py-1.5 bg-[var(--tir-burgundy)] text-white hover:bg-[var(--tir-burgundy-dark)] disabled:opacity-40 disabled:cursor-not-allowed rounded-sm"
        >
          <Send size={11} /> Send
        </button>
      </form>
    </div>
  );
}

/**
 * AthleteConversationsSection — the coach's single place to read every
 * back-and-forth with one athlete. One card per workout thread, newest
 * activity first, with the athlete's original log note for context and an
 * inline reply box. Supports ?thread={workout_id} deep-links from push
 * notifications (scrolls to + highlights the thread).
 */
export default function AthleteConversationsSection({ athleteId }) {
  const location = useLocation();
  const [threads, setThreads] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const highlightId = new URLSearchParams(location.search).get("thread");

  useEffect(() => {
    let alive = true;
    coachListConversations(athleteId)
      .then((rows) => alive && setThreads(rows))
      .catch(() => alive && setThreads([]));
    return () => {
      alive = false;
    };
  }, [athleteId]);

  if (threads === null) {
    return (
      <section className="mb-10" data-testid="athlete-conversations-section">
        <div className="text-[11px] italic text-[var(--tir-muted)]">Loading messages…</div>
      </section>
    );
  }

  const visible = showAll ? threads : threads.slice(0, 5);

  return (
    <section className="mb-10" data-testid="athlete-conversations-section" id="messages">
      <div className="flex items-baseline justify-between mb-3">
        <h2 className="text-[10px] tracking-[0.28em] uppercase font-semibold text-[var(--tir-muted)] inline-flex items-center gap-1.5">
          <MessageCircle size={11} /> Messages
          {threads.length > 0 && (
            <span className="font-mono-tir text-[var(--tir-burgundy)] normal-case tracking-normal">
              · {threads.length}
            </span>
          )}
        </h2>
        <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--tir-muted)]">
          Every conversation, in one place
        </span>
      </div>
      {threads.length === 0 ? (
        <div
          className="bg-white rounded-md border-2 border-dashed border-[var(--tir-border)] py-8 text-center text-sm text-[var(--tir-muted)]"
          data-testid="conversations-empty"
        >
          No messages yet — replies you or your athlete leave on workouts will show up here.
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((t) => (
            <ThreadCard
              key={`${t.program_id}-${t.week_number}-${t.day_label}-${t.workout_id}`}
              t={t}
              athleteId={athleteId}
              highlighted={!!highlightId && t.workout_id === highlightId}
            />
          ))}
          {threads.length > 5 && !showAll && (
            <button
              type="button"
              onClick={() => setShowAll(true)}
              data-testid="conversations-show-all"
              className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-navy)] hover:text-[var(--tir-ink)]"
            >
              Show all {threads.length} conversations
            </button>
          )}
        </div>
      )}
    </section>
  );
}
